import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, FileText, Clock, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeeDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/attendance/mark`,
        { status: 'present' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Attendance marked successfully');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="employee-dashboard">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-2">Welcome back! Here's your overview</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="hover:shadow-md transition-shadow" data-testid="attendance-days-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Attendance</CardTitle>
              <Calendar className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_attendance_days || 0}</div>
              <p className="text-xs text-muted-foreground">Days marked</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow" data-testid="documents-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Documents</CardTitle>
              <FileText className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_documents || 0}</div>
              <p className="text-xs text-muted-foreground">Uploaded files</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow" data-testid="leaves-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Paid Leaves</CardTitle>
              <Clock className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.paid_leaves_remaining || 0}</div>
              <p className="text-xs text-muted-foreground">Days remaining</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow" data-testid="salary-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Current Salary</CardTitle>
              <Briefcase className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₹{stats?.current_month_salary?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-muted-foreground">This month</p>
            </CardContent>
          </Card>
        </div>

        <Card data-testid="mark-attendance-card">
          <CardHeader>
            <CardTitle>Mark Attendance</CardTitle>
            <CardDescription>Mark your attendance for today</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.attendance_marked_today ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                  <Calendar className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-lg font-medium">Attendance Already Marked</p>
                <p className="text-sm text-muted-foreground mt-1">You've already marked your attendance for today</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Button 
                  size="lg" 
                  onClick={handleMarkAttendance}
                  data-testid="mark-attendance-button"
                >
                  <Clock className="mr-2 h-4 w-4" />
                  Mark Attendance Now
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="cursor-pointer hover:border-accent/50 transition-all" onClick={() => navigate('/attendance')} data-testid="view-attendance-card">
            <CardHeader>
              <CardTitle>Attendance History</CardTitle>
              <CardDescription>View your complete attendance records</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                View History
              </Button>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-accent/50 transition-all" onClick={() => navigate('/payslips')} data-testid="view-payslips-card">
            <CardHeader>
              <CardTitle>Payslips</CardTitle>
              <CardDescription>Download your monthly payslips</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                View Payslips
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
